import React, { useState, useEffect } from 'react';
import { FaStar } from 'react-icons/fa';
import { getTeacherRatingBreakdown } from '../bookingService';

/**
 * Rating breakdown component showing distribution of ratings
 * Displays count and percentage for each star level (5, 4, 3, 2, 1)
 */
export default function RatingBreakdown({ teacherEmail, isArabic = false }) {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchBreakdown() {
      if (!teacherEmail) return;

      setLoading(true);
      const { data, error } = await getTeacherRatingBreakdown(teacherEmail);

      if (!error && data) {
        setBreakdown(data);
      }
      setLoading(false);
    }

    fetchBreakdown();
  }, [teacherEmail]);

  if (loading) {
    return (
      <div style={{
        padding: '16px',
        color: '#94A3B8',
        fontSize: 13,
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        {isArabic ? 'جاري التحميل...' : 'Loading...'}
      </div>
    );
  }

  if (!breakdown) return null;

  // Calculate total reviews
  const totalReviews = Object.values(breakdown).reduce((sum, count) => sum + count, 0);

  if (totalReviews === 0) {
    return (
      <div style={{
        padding: '16px',
        color: '#94A3B8',
        fontSize: 13,
        textAlign: 'center',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        {isArabic ? 'لا توجد تقييمات بعد' : 'No ratings yet'}
      </div>
    );
  }

  // Render breakdown bars - matching image design (horizontal bars, 5 stars longest)
  const renderBreakdownBars = () => {
    const stars = [5, 4, 3, 2, 1];

    return stars.map(star => {
      const count = breakdown[star] || 0;
      const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
      const maxCount = Math.max(...Object.values(breakdown));
      const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;

      return (
        <div key={star} style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12
        }}>
          {/* Star number label */}
          <div style={{
            minWidth: 24,
            fontSize: 14,
            color: '#111827',
            fontWeight: 600,
            textAlign: 'right',
            fontFamily: "'IBM Plex Sans Arabic', sans-serif",
            ...(isArabic ? { textAlign: 'left' } : {})
          }}>
            {star}
          </div>

          {/* Progress bar - yellow/gray based on count */}
          <div style={{
            flex: 1,
            height: 24,
            background: '#f3f4f6',
            borderRadius: 4,
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              width: `${barWidth}%`,
              height: '100%',
              background: count > 0 ? '#fcd34d' : '#f3f4f6',
              borderRadius: 4,
              transition: 'width 0.3s ease',
              minWidth: count > 0 ? '4px' : '0px'
            }} />
          </div>
        </div>
      );
    });
  };

  return (
    <div style={{
      padding: '16px 0'
    }}>
      {/* Breakdown bars - matching image design (header removed, displayed in parent component) */}
      {renderBreakdownBars()}
    </div>
  );
}
